import React, { useState } from "react";
import styled from "@emotion/styled";
import PreviewSection from "../components/PreviewSection";
import CodeBlock from "../components/CodeBlock";
import ContentPageNav from "../../../components/navigation/ContentPageNav";
import {
  SideBarContainer,
  SideBarUl,
  ActiveLink,
  InactiveLink,
} from "@atlas-int/ui";
import {
  DetailsPageContainer,
  Details,
  DetailsSummary,
  DetailsContent,
  ToggleIcon,
} from "../../../components/layout/StaticPageLayout";
import {
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  border as borderToken,
  text,
} from "@atlas-int/tokens";

const SubSection = styled.div`
  margin-bottom: ${spacing[12]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Description = styled.p`
  font-size: ${fontSize.base};
  color: ${text.secondary};
  margin: 0 0 ${spacing[6]} 0;
`;

const NavContainer = styled.div`
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  overflow: hidden;
  position: relative;
`;

const SidebarDemoContainer = styled.div`
  display: flex;
  gap: ${spacing[10]};
  align-items: flex-start;
`;

const SidebarPreview = styled.div`
  width: 220px;
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  overflow: hidden;
`;

const DetailsPreview = styled.div`
  width: 100%;
  max-width: 600px;
`;

// ─── ContentPageNav Demo ────────────────────────────────────────────────────

const ContentPageNavDemo: React.FC = () => (
  <SubSection>
    <SubTitle>ContentPageNav</SubTitle>
    <Description>
      60px-tall navigation bar used on content pages (landing, about, FAQ,
      glossary). Includes the Growth Lab logo and flyout menu items.
    </Description>
    <NavContainer>
      <ContentPageNav />
    </NavContainer>
    <CodeBlock
      code={`import { ContentPageNav, navBarHeight } from "components";

// navBarHeight = "60px"
<ContentPageNav />

// On the home page, pass isHomePage for a larger logo
<ContentPageNav isHomePage />`}
    />
  </SubSection>
);

// ─── Sidebar Nav Demo ───────────────────────────────────────────────────────

const SidebarNavDemo: React.FC = () => (
  <SubSection>
    <SubTitle>Sidebar Navigation</SubTitle>
    <Description>
      Sticky sidebar used on FAQ, glossary, and content pages. Built from
      SideBarContainer, SideBarUl, ActiveLink, and InactiveLink primitives.
    </Description>
    <SidebarDemoContainer>
      <SidebarPreview>
        <SideBarContainer style={{ position: "relative", marginTop: 0 }}>
          <SideBarUl>
            <ActiveLink>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Overview
              </a>
            </ActiveLink>
            <InactiveLink>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Methodology
              </a>
            </InactiveLink>
            <InactiveLink>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Data Sources
              </a>
            </InactiveLink>
            <InactiveLink>
              <a href="#" onClick={(e) => e.preventDefault()}>
                FAQ
              </a>
            </InactiveLink>
          </SideBarUl>
        </SideBarContainer>
      </SidebarPreview>
    </SidebarDemoContainer>
    <CodeBlock
      code={`import { Sidebar } from "components";

const items = [
  { id: "overview", label: "Overview" },
  { id: "methodology", label: "Methodology" },
  { id: "data-sources", label: "Data Sources" },
];

<Sidebar activeHeadingId="overview" items={items} />`}
    />
  </SubSection>
);

// ─── Details/Summary Demo ───────────────────────────────────────────────────

const DetailsSummaryDemo: React.FC = () => {
  const [open1, setOpen1] = useState(false);
  const [open2, setOpen2] = useState(false);

  return (
    <SubSection>
      <SubTitle>Details / Summary</SubTitle>
      <Description>
        Native HTML details/summary pattern with Atlas styling. Used on FAQ and
        glossary pages for expandable content sections.
      </Description>
      <DetailsPreview>
        <DetailsPageContainer>
          <Details open={open1} onToggle={() => setOpen1(!open1)}>
            <DetailsSummary>
              What is the Atlas of Economic Complexity?
              <ToggleIcon isOpen={open1} />
            </DetailsSummary>
            <DetailsContent>
              <p>
                The Atlas of Economic Complexity is a research and data
                visualization tool that allows people to explore global trade
                flows across markets, track these dynamics over time, and
                discover new growth opportunities.
              </p>
            </DetailsContent>
          </Details>
          <Details open={open2} onToggle={() => setOpen2(!open2)}>
            <DetailsSummary>
              How is economic complexity calculated?
              <ToggleIcon isOpen={open2} />
            </DetailsSummary>
            <DetailsContent>
              <p>
                Economic complexity is calculated using trade data to measure the
                diversity and sophistication of a country's productive output
                using the Method of Reflections on the country-product bipartite
                network.
              </p>
            </DetailsContent>
          </Details>
        </DetailsPageContainer>
      </DetailsPreview>
      <CodeBlock
        code={`import {
  DetailsPageContainer,
  Details,
  DetailsSummary,
  DetailsContent,
  ToggleIcon,
} from "components";

<DetailsPageContainer>
  <Details open={isOpen} onToggle={() => setIsOpen(!isOpen)}>
    <DetailsSummary>
      Question title
      <ToggleIcon isOpen={isOpen} />
    </DetailsSummary>
    <DetailsContent>
      <p>Answer content here.</p>
    </DetailsContent>
  </Details>
</DetailsPageContainer>`}
      />
    </SubSection>
  );
};

// ─── Main Section ───────────────────────────────────────────────────────────

const StaticPageSection: React.FC = () => (
  <>
    <PreviewSection
      id="content-page-nav"
      title="ContentPageNav"
      description="Navigation bar for content pages"
    >
      <ContentPageNavDemo />
    </PreviewSection>

    <PreviewSection
      id="sidebar-nav"
      title="Sidebar Navigation"
      description="Sticky sidebar and link primitives for content pages"
    >
      <SidebarNavDemo />
    </PreviewSection>

    <PreviewSection
      id="details-summary"
      title="Details / Summary"
      description="Expandable content sections for FAQ and glossary pages"
    >
      <DetailsSummaryDemo />
    </PreviewSection>
  </>
);

export default StaticPageSection;
